import type {
  ContractorApplicant,
  ContractorApplicantStatus,
  ContractorOnboardStep,
  ContractorOnboardStepId,
} from '../types/contractorOnboard';

export const CONTRACTOR_TRAIL_LABELS: Record<ContractorOnboardStepId, string> = {
  submitted: 'Submitted',
  forms: 'Forms',
  agreement: 'Agreement',
  pool: 'Pool',
};

export function normalizeContractorEmail(email: string): string {
  return email.trim().toLowerCase();
}

export function contractorFullName(
  person: Pick<ContractorApplicant, 'firstName' | 'lastName'>,
): string {
  return `${person.firstName || ''} ${person.lastName || ''}`.replace(/\s+/g, ' ').trim();
}

export function contractorPacketApproved(applicant: Pick<ContractorApplicant, 'w9ApprovedAt' | 'photoIdApprovedAt'>): boolean {
  return Boolean(applicant.w9ApprovedAt && applicant.photoIdApprovedAt);
}

export function canConfirmCommitToRoster(
  applicant: Pick<ContractorApplicant, 'status'> | null | undefined,
): boolean {
  return applicant?.status === 'pooled';
}

/** Staff may grandfather anyone who is not already in the pool. */
export function canGrandfatherContractorIntoPool(
  applicant: Pick<ContractorApplicant, 'status'> | null | undefined,
): boolean {
  return Boolean(applicant && applicant.status !== 'pooled');
}

export function uniqueIds(ids: Array<string | null | undefined>): string[] {
  return Array.from(new Set(ids.map(id => String(id || '').trim()).filter(Boolean)));
}

/** Resume a declined applicant from the furthest completed work, not from scratch. */
export function resumeApplicantStatus(applicant: {
  status: ContractorApplicantStatus;
  formsRequestedAt?: Date | null;
  formsApprovedAt?: Date | null;
  w9ApprovedAt?: Date | null;
  photoIdApprovedAt?: Date | null;
  agreementId?: string | null;
  contractorUserId?: string | null;
}): ContractorApplicantStatus {
  if (applicant.status === 'pooled' || applicant.contractorUserId) return 'pooled';
  if (applicant.status === 'agreement_sent' || applicant.agreementId) {
    return applicant.status === 'agreement_sent' ? 'agreement_sent' : 'forms_approved';
  }
  if (
    applicant.status === 'forms_approved'
    || applicant.formsApprovedAt
    || contractorPacketApproved(applicant)
  ) {
    return 'forms_approved';
  }
  if (applicant.status === 'forms_requested' || applicant.formsRequestedAt) return 'forms_requested';
  return 'submitted';
}

export function mergeApplicantFromCommit(input: {
  existing: ContractorApplicant | null;
  commitId: string;
  jobId: string;
  jobTitle?: string | null;
  firstName: string;
  lastName: string;
  email: string;
  phone?: string;
  publicSlug: string;
  now?: Date;
}): ContractorApplicant {
  const now = input.now || new Date();
  const emailNormalized = normalizeContractorEmail(input.email);
  if (!input.existing) {
    return {
      id: '',
      firstName: input.firstName.trim(),
      lastName: input.lastName.trim(),
      email: emailNormalized,
      emailNormalized,
      phone: (input.phone || '').trim() || undefined,
      status: 'submitted',
      sourceCommitIds: uniqueIds([input.commitId]),
      jobIds: uniqueIds([input.jobId]),
      lastJobId: input.jobId || null,
      lastJobTitle: input.jobTitle || null,
      publicSlug: input.publicSlug,
      createdAt: now,
      updatedAt: now,
    };
  }

  const existing = input.existing;
  const status = existing.status === 'declined'
    ? resumeApplicantStatus(existing)
    : existing.status;

  return {
    ...existing,
    firstName: input.firstName.trim() || existing.firstName,
    lastName: input.lastName.trim() || existing.lastName,
    phone: (input.phone || '').trim() || existing.phone,
    status,
    sourceCommitIds: uniqueIds([...existing.sourceCommitIds, input.commitId]),
    jobIds: uniqueIds([...existing.jobIds, input.jobId]),
    lastJobId: input.jobId || existing.lastJobId || null,
    lastJobTitle: input.jobTitle || existing.lastJobTitle || null,
    updatedAt: now,
  };
}

export function deriveContractorPipeline(
  applicant: Pick<ContractorApplicant, 'status'> | null | undefined,
): ContractorOnboardStep[] {
  const status = applicant?.status || 'submitted';
  const declined = status === 'declined';
  const pastSubmitted = status !== 'submitted' && status !== 'declined';
  const formsApproved = status === 'forms_approved'
    || status === 'agreement_sent'
    || status === 'pooled';
  const signed = status === 'pooled';

  const step = (
    id: ContractorOnboardStepId,
    complete: boolean,
    currentWhen: boolean,
  ): ContractorOnboardStep => {
    if (declined && !complete) {
      return { id, label: CONTRACTOR_TRAIL_LABELS[id], state: 'locked' };
    }
    if (complete) return { id, label: CONTRACTOR_TRAIL_LABELS[id], state: 'complete' };
    if (currentWhen) return { id, label: CONTRACTOR_TRAIL_LABELS[id], state: 'current' };
    return { id, label: CONTRACTOR_TRAIL_LABELS[id], state: 'upcoming' };
  };

  return [
    {
      id: 'submitted',
      label: CONTRACTOR_TRAIL_LABELS.submitted,
      state: declined || pastSubmitted ? 'complete' : 'current',
    },
    step('forms', formsApproved, status === 'forms_requested'),
    step('agreement', signed, formsApproved && !signed),
    step('pool', signed, false),
  ];
}

export function contractorTrailBadge(
  applicant: Pick<ContractorApplicant, 'status'> | null | undefined,
): { label: string; state: ContractorOnboardStep['state'] } {
  const steps = deriveContractorPipeline(applicant);
  const current = steps.find(s => s.state === 'current') || steps.find(s => s.state === 'complete');
  if (applicant?.status === 'pooled') return { label: 'Pool', state: 'complete' };
  if (applicant?.status === 'declined') return { label: 'Declined', state: 'rejected' };
  return { label: current?.label || 'Submitted', state: current?.state || 'upcoming' };
}

export function crewOnboardPublicPath(slug: string): string {
  return `/crew-onboard/${slug}`;
}

export function crewOnboardPublicUrl(slug: string): string {
  const path = crewOnboardPublicPath(slug);
  if (typeof window === 'undefined' || !window.location?.origin) return path;
  return `${window.location.origin}${path}`;
}

export function createContractorOnboardSlug(): string {
  const bytes = new Uint8Array(16);
  crypto.getRandomValues(bytes);
  return Array.from(bytes, b => b.toString(16).padStart(2, '0')).join('');
}

export function shouldNotifyCrewOnboardStaff(user: {
  isActive?: boolean;
  role?: unknown;
  designations?: unknown;
  briefingProfile?: string | null;
}): boolean {
  if (user.isActive === false) return false;
  const role = String(user.role || '').trim().toLowerCase();
  if (role === 'employee' || role === 'admin' || role === 'employee_office') return true;
  if (role !== 'employee' && role !== 'admin' && role !== 'employee_office') {
    const designations = Array.isArray(user.designations) ? user.designations.map(d => String(d)) : [];
    if (designations.includes('project_manager') || designations.includes('coo')) return true;
    return user.briefingProfile === 'coo';
  }
  return false;
}

/** Employee — Office staff who set up 1099s in QuickBooks after pool approval. */
export function shouldNotifyContractorQuickBooksSetup(user: {
  isActive?: boolean;
  role?: unknown;
  designations?: unknown;
}): boolean {
  if (user.isActive === false) return false;
  return String(user.role || '').trim().toLowerCase() === 'employee_office';
}

export function crewOnboardNotificationLink(opts: {
  applicantId?: string | null;
  jobId?: string | null;
}): string {
  const params = new URLSearchParams({ tab: 'crew' });
  if (opts.jobId) params.set('job', opts.jobId);
  if (opts.applicantId) params.set('applicant', opts.applicantId);
  return `/people/hiring?${params.toString()}`;
}
